/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import useFacility from '../../hooks/useFacility';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const toInputDate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const PRESETS = [
  {
    label: 'Year to date',
    range: () => { const now = new Date(); return [new Date(now.getFullYear(), 0, 1), now]; }
  },
  {
    label: 'This year',
    range: () => { const y = new Date().getFullYear(); return [new Date(y, 0, 1), new Date(y, 11, 31)]; }
  },
  {
    label: 'Last year',
    range: () => { const y = new Date().getFullYear() - 1; return [new Date(y, 0, 1), new Date(y, 11, 31)]; }
  },
  {
    label: 'Last 12 months',
    range: () => { const end = new Date(); const start = new Date(end); start.setFullYear(end.getFullYear() - 1); start.setDate(start.getDate() + 1); return [start, end]; }
  }
];

const num = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const pct = (p) => (p == null ? '—' : `${Number(p).toFixed(2)}%`);

const barColor = (p) => {
  if (p == null) return 'inherit';
  if (p > 100) return 'error';
  if (p >= 75) return 'success';
  if (p >= 40) return 'primary';
  return 'warning';
};

const UseBar = ({ value, width = 90 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, value || 0)}
      color={barColor(value)}
      sx={{ width, height: 8, borderRadius: 4 }}
    />
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 58 }}>{pct(value)}</Typography>
  </Box>
);

const csvCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const CompartmentRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          {row.projects.length > 0 && (
            <IconButton size="small" aria-label={open ? 'Hide projects' : 'Show projects'} onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{row.chamber}</Typography>
          {row.description && <Typography variant="caption" color="text.secondary">{row.description}</Typography>}
          {row.status !== 'Active' && <Chip label={row.status} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
        </TableCell>
        <TableCell>{row.facility_name}</TableCell>
        <TableCell>{row.unit}</TableCell>
        <TableCell align="right">{num(row.max_capacity)}</TableCell>
        <TableCell align="right">{num(row.capacity_unit_days)}</TableCell>
        <TableCell align="right">{num(row.used_unit_days)}</TableCell>
        <TableCell>{row.max_capacity > 0 ? <UseBar value={row.percent} /> : <Typography variant="caption" color="text.secondary">No capacity set</Typography>}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, ml: 6 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project ID</TableCell>
                    <TableCell>Project Title</TableCell>
                    <TableCell align="right">{row.unit_short}-days used</TableCell>
                    <TableCell>Share of compartment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.projects.map(p => (
                    <TableRow key={`${p.project_id}-${p.project_title}`}>
                      <TableCell>{p.project_id}</TableCell>
                      <TableCell>{p.project_title}</TableCell>
                      <TableCell align="right">{num(p.used_unit_days)}</TableCell>
                      <TableCell><UseBar value={p.percent} width={70} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const COLUMNS = [
  { id: 'chamber', label: 'Compartment' },
  { id: 'facility_name', label: 'Facility' },
  { id: 'unit', label: 'Unit' },
  { id: 'max_capacity', label: 'Max Capacity', align: 'right' },
  { id: 'capacity_unit_days', label: 'Capacity (unit-days)', align: 'right' },
  { id: 'used_unit_days', label: 'Used (unit-days)', align: 'right' },
  { id: 'percent', label: 'Space Use' }
];

const SpaceUseReport = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();

  const [initialStart, initialEnd] = PRESETS[0].range();
  const [startDate, setStartDate] = useState(toInputDate(initialStart));
  const [endDate, setEndDate] = useState(toInputDate(initialEnd));
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [facilities, setFacilities] = useState([]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('percent');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!prefLoading && facilityPreference) setSelectedFacility(facilityPreference);
  }, [facilityPreference, prefLoading]);

  useEffect(() => {
    axios.get('/api/facilities')
      .then(res => setFacilities(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error('Failed to fetch facilities:', err));
  }, []);

  const datesValid = startDate && endDate && startDate <= endDate;

  const fetchReport = useCallback(async () => {
    if (prefLoading || !datesValid) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedFacility !== 'all') params.append('facility', selectedFacility);
      if (includeInactive) params.append('include_inactive', 'true');
      const res = await axios.get(`/api/admin/reports/space-use?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      console.error('Error fetching space use report:', err);
      setError(err.response?.data?.error || 'Failed to load the space use report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [prefLoading, datesValid, startDate, endDate, selectedFacility, includeInactive]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, report, sortColumn, sortDirection]);

  const compartments = useMemo(() => {
    if (!report) return [];
    const q = searchQuery.trim().toLowerCase();
    const rows = report.facilities.flatMap(f =>
      f.compartments.map(c => ({ ...c, facility_name: f.facility_name }))
    ).filter(c => !q || [c.chamber, c.description, c.facility_name, c.unit, ...c.projects.map(p => p.project_title), ...c.projects.map(p => p.project_id)]
      .some(v => v && String(v).toLowerCase().includes(q)));

    const dir = sortDirection === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const av = a[sortColumn] ?? -1;
      const bv = b[sortColumn] ?? -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [report, searchQuery, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(['chamber', 'facility_name', 'unit'].includes(column) ? 'asc' : 'desc');
    }
  };

  const applyPreset = (preset) => {
    const [s, e] = preset.range();
    setStartDate(toInputDate(s));
    setEndDate(toInputDate(e));
  };

  const handleExport = () => {
    const header = ['Facility', 'Compartment', 'Description', 'Status', 'Unit', 'Max Capacity', 'Days in Period',
      'Capacity (unit-days)', 'Used (unit-days)', 'Space Use %', 'Project ID', 'Project Title', 'Project Used (unit-days)', 'Project Share %'];
    const rows = [];
    for (const c of compartments) {
      const base = [c.facility_name, c.chamber, c.description, c.status, c.unit, c.max_capacity, report.period.days,
        c.capacity_unit_days, c.used_unit_days, c.percent ?? ''];
      if (c.projects.length === 0) rows.push([...base, '', '', '', '']);
      for (const p of c.projects) rows.push([...base, p.project_id, p.project_title, p.used_unit_days, p.percent ?? '']);
    }
    const content = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `facility-space-use-${startDate}-to-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
        Facility Space Use Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Space use = space-days used ÷ space-days available. A 12 sq ft compartment over 365 days offers
        4,380 sq ft-days; a 10 sq ft assignment for 100 of those days uses 1,000 sq ft-days, or 22.83%.
        A facility's figure is the total used over the total available across its compartments, kept
        separate for each unit of measure. Assignments with no end date count through the end of the period.
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Facility</InputLabel>
          <Select value={selectedFacility} label="Facility" onChange={(e) => setSelectedFacility(e.target.value)}>
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(f => <MenuItem key={f._id} value={f._id}>{f.facility_name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
          error={Boolean(startDate && endDate && startDate > endDate)}
        />
        <FormControlLabel
          control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
          label="Include inactive compartments"
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh data">
          <span>
            <IconButton onClick={fetchReport} disabled={loading || !datesValid}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" variant="outlined" onClick={() => applyPreset(p)} />
        ))}
      </Box>

      {!datesValid && (
        <Alert severity="warning" sx={{ mb: 2 }}>Choose a start date on or before the end date.</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {!report && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      )}

      {report && (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {report.period.days} day{report.period.days === 1 ? '' : 's'} in period
          </Typography>

          {/* Facility summary */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {report.facilities.length === 0 && (
              <Typography color="text.secondary">No compartments match these filters.</Typography>
            )}
            {report.facilities.map(f => (
              <Paper key={f.facility_id} sx={{ p: 2, flex: '1 1 320px', maxWidth: 560 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{f.facility_name}</Typography>
                {f.units.map(u => (
                  <Box key={u.unit} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="body2" color="text.secondary">
                        {u.unit} · {u.compartments} compartment{u.compartments === 1 ? '' : 's'} · {num(u.capacity)} {u.unit_short}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>{pct(u.percent)}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, u.percent || 0)}
                      color={barColor(u.percent)}
                      sx={{ height: 10, borderRadius: 5, my: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {num(u.used_unit_days)} of {num(u.capacity_unit_days)} {u.unit_short}-days used
                    </Typography>
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>

          {/* Compartments */}
          <Paper>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Compartments ({compartments.length})</Typography>
              <TextField
                placeholder="Search compartment or project..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ width: 300 }}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
              />
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={compartments.length === 0}>
                Export CSV
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5', '& .MuiTableCell-root': { fontWeight: 600 } }}>
                    <TableCell padding="checkbox" />
                    {COLUMNS.map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel
                          active={sortColumn === col.id}
                          direction={sortColumn === col.id ? sortDirection : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {compartments
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(c => <CompartmentRow key={c.chamber_id} row={c} />)}
                </TableBody>
              </Table>
            </TableContainer>
            {compartments.length > 0 && (
              <TablePagination
                component="div"
                count={compartments.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default SpaceUseReport;
